import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static values = {
    text: String,
  };

  declare textValue: string;

  async copy(event: Event): Promise<void> {
    event.preventDefault();
    // currentTarget is null once the event has finished dispatching, i.e. after the await below
    const button = event.currentTarget as HTMLElement;

    const textToCopy = this.textValue || this.getInputValue();

    if (!textToCopy) {
      return;
    }

    try {
      await navigator.clipboard.writeText(textToCopy);
      this.showFeedback(button);
    } catch {
      // Fallback for older browsers
      this.fallbackCopy(textToCopy);
      this.showFeedback(button);
    }
  }

  private getInputValue(): string {
    const input = this.element.querySelector('input') as HTMLInputElement;
    return input?.value || '';
  }

  private fallbackCopy(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  }

  private showFeedback(button: HTMLElement): void {
    const originalContent = button.innerHTML;
    const originalTooltip = button.getAttribute('data-tip');

    // Update tooltip if present
    if (originalTooltip) {
      button.setAttribute('data-tip', 'Copied!');
    }

    // Brief visual feedback
    button.classList.add('btn-success');

    // Show checkmark temporarily
    const isIconOnly = button.classList.contains('btn-square');
    if (isIconOnly) {
      button.innerHTML = `
        <svg class="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
        </svg>
      `;
    } else {
      button.innerHTML = `
        <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
        </svg>
        Copied!
      `;
    }

    // Restore original state after delay
    setTimeout(() => {
      button.classList.remove('btn-success');
      button.innerHTML = originalContent;
      if (originalTooltip) {
        button.setAttribute('data-tip', originalTooltip);
      }
    }, 1500);
  }
}
